import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/controller';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';

import { Box, Stack, SxProps, Theme, useTheme } from '@mui/material';
import {
  useInternetStatus,
  useRouterProtector,
  useSelectInitialCity,
  useStateObserver,
} from '@/logic/hooks';

import { EnvironmentInfo } from './environment-info';

import type { NextPage } from 'next';
import { FC } from 'react';
import { Props } from '../../@types';
import dynamic from 'next/dynamic';
import shallow from 'zustand/shallow';
import { useWalletBalance } from '@/features/wallet/hooks/use-wallet-balance';
import { useCities } from '@/logic/state';
import { useGetCities } from '@/services';
import { useDataLayer } from '@/logic/hooks/use-data-layer';
import { Navbar } from '../navbar/navbar';

const Umbrella = dynamic(
  () => import('./umbrella').then(component => component.Umbrella),

  { ssr: false }
);

// Split out of the main bundle so its JS (icons, links) does not have to
// parse/execute before hydration can start — it is always below the fold and
// non-interactive-critical. Kept SSR'd (ssr: true, the default) so it still
// ships in the initial HTML and does not shift layout in on mobile.
const Footer = dynamic(() => import('../footer/footer').then(m => m.Footer));

// TODO: place useGetCities in proper place and hide all website layout until cities list is available

// App-wide side effects. They subscribe to city, user and router state, so they
// live in their own component: when they lived in Layout, every city or user
// change re-rendered Layout and with it the whole navbar, side menu and footer
// (~1,000 components, twice per city-picker "Select" — measured). Rendered last
// in Layout so their effects still run after the rest of the tree's, as before.
const LayoutEffects: FC = () => {
  const setCities = useCities(s => s.setCities, shallow);
  useInternetStatus();
  useRouterProtector();
  useStateObserver();
  useSelectInitialCity();
  // useOneSignal();
  // useAdBlockDetector();
  useWalletBalance();
  useDataLayer();

  useGetCities(
    {},
    {
      onSuccess: data =>
        setCities({
          citiesList: data.city,
        }),
    }
  );

  return null;
};

export const Layout: NextPage<Props> = ({ children }) => {
  const theme = useTheme();

  const sx: Partial<SxProps<Theme>> = {
    backgroundColor: theme.palette.layout.backgroundColor,
  };

  return (
    <Box sx={sx}>
      <Stack
        sx={{
          minHeight: '100vh',
        }}
      >
        <EnvironmentInfo />
        <Navbar />
        {/*
          Pages fill in client-fetched content (showtimes, lists) after
          hydration. While <main> is shorter than the viewport the footer sits
          on screen, and every bit of growth shoves it down — CLS 0.27 on a
          movie page. Keeping <main> at least a viewport tall parks the footer
          below the fold, where its movement doesn't count.
        */}
        <Box id='layout' component='main' sx={{ flexGrow: 1, minHeight: '100svh' }}>
          {children}
        </Box>
        <Footer />
      </Stack>
      <Umbrella />
      <LayoutEffects />
    </Box>
  );
};
